import { memo, useEffect } from 'react';
import { Divider, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DeviceWrapper, Flex } from '@/components';
import { useModal, useSingleSelfRedirect } from '@/hooks';
import { useAppContext } from '@/context';
import { useSelfContext } from '@/context/SelfContext';
import {
  SpoonForkSVG,
  BallStrokeSVG,
  DoctorSVG,
  BusSVG,
} from '@/components/common/AppIcons';
import SelfListModal from '../../modals/SelfListModal';
import { envConfig } from '@/configs';

const getSelfIcon = (variant: ComponentVariant) =>
({
  sport: BallStrokeSVG,
  nurture: SpoonForkSVG,
  health: DoctorSVG,
  transport: BusSVG,
}[variant]);

export interface ReserveHeaderProps {
  mobileButton?: any;
  modalDefaultVisible?: boolean;
  isNewReserveMode: boolean;
  setIsNewReserveMode: (value: boolean) => void;
}

export const ReserveHeader = memo(({ mobileButton, modalDefaultVisible = true, isNewReserveMode = false, setIsNewReserveMode }: ReserveHeaderProps) => {

  const modal = useModal();
  const navigate = useNavigate();
  const [t] = useTranslation(['common']);
  const { mode } = useAppContext();
  const { currentSelf, selfText, redirectToSelf } = useSelfContext();

  useEffect(() => {
    if (!currentSelf && modalDefaultVisible) modal.show();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalDefaultVisible]);

  useSingleSelfRedirect();

  const prefix = selfText('choose-self-prefix');

  const SelfIconSVG = getSelfIcon(mode);
  const conf = envConfig.onPremise;

  return (
    <>
      <DeviceWrapper show={['desktop']}>
        <SelfListModal {...modal} onSelect={modal.hide} />
        <Flex
          className="w-[60%] h-[64px] mt-4 mx-auto mb-3"
          direction="row"
          align="center"
          justify="center"
        >
          <SelfIconSVG width={30} height={30} />
          <div
            onClick={modal.show}
            className={'flex-center p-2 rounded-lg border-[0.5px] border-b3 cursor-pointer w-40 my-2'}
          >
            <span className="flex-fill text-start mx-2">
              {currentSelf?.name || t('common:messages.choose-your-desired-typeof', { value: prefix })}
            </span>
            <span className="flaticon-left-chevron icon-xs" />
          </div>
        </Flex>
        {
          conf.core.changableReserveMode === true && (
            <Select
              size="large"
              className="select-gateway"
              value={isNewReserveMode ? "true" : "false"}
              onChange={value => setIsNewReserveMode(value === "true")}
              placeholder={t('reserve:labels.reserve-mode')}
            >
              <Select.Option key={"false"}>
                {t('reserve:labels.default-reserve-mode')}
              </Select.Option>
              <Select.Option key={"true"}>
                {t('reserve:labels.new-reserve-mode')}
              </Select.Option>
            </Select>
          )
        }
      </DeviceWrapper>
      <DeviceWrapper show={['mobile']}>
        <Flex className="w-full px-4" direction="column" noGap>
          <Flex
            direction="row"
            className="w-full h-[64px] mx-auto"
            align="center"
            justify="center"
          >
            <SelfIconSVG width={35} height={35} />
            <div
              onClick={() => redirectToSelf({ mode, navigate })}
              className={'flex-center p-1.5 rounded-lg border-[0.5px] border-b3 cursor-pointer w-full my-2'}
            >
              <span className="flex-fill text-start mx-2 text-sm">
                {currentSelf?.name || t('common:messages.choose-your-desired-typeof', { value: prefix })}
              </span>
              <span className="flaticon-left-chevron icon-xs inline-flex leading-[0]" />
            </div>
            <span className="ms-auto">{mobileButton}</span>
          </Flex>
          <Divider className="!mt-0" />
        </Flex>
      </DeviceWrapper>
    </>
  );
});

export default ReserveHeader;
